import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {SkeletonBrick} from '@Components/skeleton-brick/skeleton-brick';
import type {SelectedImageAsset} from '@Components/ai-chat-window/ai-chat-window.types';
import {SelectedImageAssetType} from '@Components/ai-chat-window/ai-chat-window.types';
import {getUrlForGeneratedAiChatModelAssetsImage} from '@Libraries/ai-chat.library';
import {getUserImageThumbUrl} from '@Components/ai-chat-window/lib/chat-attachment-image.library';
import {getPosterThumbnailPreviewURL} from '@Libraries/poster-library';
import {getAssetUrl as getStaticAssetUrl} from '@Utils/s3.util';
import {MediaItemSizeType} from '@Libraries/s3-library';
import TooltipTrigger from '@Components/tooltip-trigger';
import {TOOLTIP_POSITION} from '@Components/tooltip';
import styles from './selected-asset-thumb.module.scss';
import {SELECTED_ASSET_THUMB_SIZE_PX} from './selected-asset-thumb.types';
import type {SelectedAssetThumbProps} from './selected-asset-thumb.types';

const getInitialThumbUrl = (asset: SelectedImageAsset): string | null => {
  switch (asset.assetType) {
    case SelectedImageAssetType.USER:
      return getUserImageThumbUrl(asset.hashedId, asset.extension);
    case SelectedImageAssetType.MODEL:
      return getUrlForGeneratedAiChatModelAssetsImage(asset.hashedId, MediaItemSizeType.THUMB);
    case SelectedImageAssetType.EXAMPLE:
      return getStaticAssetUrl(`${asset.hashedId}-thumb`);
    case SelectedImageAssetType.DESIGN:
      return null;
  }
};

const getAssetDetailsTooltip = (asset: SelectedImageAsset): string => {
  const extensionValue = asset.extension.length === 0 ? '(none)' : asset.extension;
  const lines = [`Type: ${asset.assetType}`, `Hashed ID: ${asset.hashedId}`, `Extension: ${extensionValue}`];
  if (asset.pageHashedId !== null && asset.pageHashedId !== undefined && asset.pageHashedId.length > 0) {
    lines.push(`Page hashed ID: ${asset.pageHashedId}`);
  }
  if (asset.messageId !== null) {
    lines.push(`Message ID: ${asset.messageId}`);
  }
  return lines.join('\n');
};

export function SelectedAssetThumb({asset}: SelectedAssetThumbProps): ReactElement {
  const [thumbUrl, setThumbUrl] = useState<string | null>(() => getInitialThumbUrl(asset));
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
    setThumbUrl(getInitialThumbUrl(asset));

    if (asset.assetType !== SelectedImageAssetType.DESIGN) {
      return;
    }

    void (async (): Promise<void> => {
      const url = await getPosterThumbnailPreviewURL(asset.hashedId, asset.pageHashedId ?? undefined);
      if (url.length === 0) {
        setHasError(true);
        return;
      }
      setThumbUrl(url);
    })();
  }, [asset.assetType, asset.hashedId, asset.pageHashedId, asset.extension]);

  const detailsTooltip = getAssetDetailsTooltip(asset);

  if (hasError) {
    return (
      <TooltipTrigger text={detailsTooltip} position={TOOLTIP_POSITION.BOTTOM_MIDDLE} tooltipClass={styles.detailsTooltip} overlayClass={styles.tooltipOverlay}>
        <div className={`radius-8 ${styles.errorPlaceholder}`}>{'Failed to load image'}</div>
      </TooltipTrigger>
    );
  }

  if (thumbUrl === null) {
    return <SkeletonBrick className={'radius-8'} width={SELECTED_ASSET_THUMB_SIZE_PX} height={SELECTED_ASSET_THUMB_SIZE_PX} />;
  }

  return (
    <TooltipTrigger text={detailsTooltip} position={TOOLTIP_POSITION.BOTTOM_MIDDLE} tooltipClass={styles.detailsTooltip} overlayClass={styles.tooltipOverlay}>
      <a href={thumbUrl} target="_blank" rel="noreferrer">
        <img
          src={thumbUrl}
          alt=""
          aria-hidden="true"
          className={`radius-8 ${styles.thumb}`}
          onError={(): void => {
            setHasError(true);
          }}
        />
      </a>
    </TooltipTrigger>
  );
}
